import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '../config';

const backendApi = config.backendApi;

export interface IdentityEmail {
  id: string;
  email: string;
  verified: boolean;
  primary: boolean;
  createdAt: string;
}

export function useIdentityEmails() {
  return useQuery({
    queryKey: ['identity-emails'],
    queryFn: async () => {
      const response = await axios.get<{ emails: IdentityEmail[] }>(`${backendApi}/api/account/emails`, {
        withCredentials: true,
      });
      return response.data.emails;
    },
  });
}

export function useAddIdentityEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      axios.post(`${backendApi}/api/account/emails`, { email }, { withCredentials: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['identity-emails'] }),
  });
}

export function useVerifyIdentityEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      axios.post(`${backendApi}/api/account/emails/verify`, { token }, { withCredentials: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['identity-emails'] });
      void queryClient.invalidateQueries({ queryKey: ['notification-addresses'] });
    },
  });
}

export function useSetPrimaryIdentityEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      axios.post(`${backendApi}/api/account/emails/${id}/primary`, {}, { withCredentials: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['identity-emails'] }),
  });
}

export function useResendIdentityEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      axios.post(`${backendApi}/api/account/emails/${id}/resend`, {}, { withCredentials: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['identity-emails'] }),
  });
}

export function useDeleteIdentityEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      axios.delete(`${backendApi}/api/account/emails/${id}`, { withCredentials: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['identity-emails'] }),
  });
}
